import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import PublicationRecord from "models/view-models/publication-record";
import UserPublicationPinRecord from "models/view-models/user-publication-pin-record";
import { ModalTypes } from "molecules/constants/modal-types";
import { SearchFormStyles } from "molecules/enums/search-form-style";
import SearchForm from "molecules/forms/search-form";
import Modal from "molecules/modals/modal";
import PublicationListSearchContents from "organisms/publication-lists/publication-list-search-contents";
import React, {
    ChangeEvent,
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";
import { CollectionUtils } from "utilities/collection-utils";
import useNetworkInformation from "utilities/contexts/network-information/use-network-information";
import { Breakpoints } from "utilities/enumerations/breakpoints";
import usePublicationCodeGroups from "utilities/hooks/domain/publications/use-publication-code-groups";
import usePublicationFavorites from "utilities/hooks/domain/user-publication-favorites/use-publication-favorites";
import useBreakpoint, {
    BreakpointComparer,
} from "utilities/hooks/use-breakpoint";
import useOutsideClick from "utilities/hooks/use-outside-click";
import { t } from "utilities/localization-utils";

const CSS_BASE_CLASS_NAME = "c-publication-list-search";
interface PublicationListSearchProps {
    isOpen: boolean;
    handleOpen: () => void;
    handleClose: () => void;
    includeLegacy?: boolean;
    onPublicationClick: (publication: PublicationRecord) => void;
    pins?: UserPublicationPinRecord[];
    onChangePins: (pins: UserPublicationPinRecord[]) => void;
}

const PublicationListSearch = (props: PublicationListSearchProps) => {
    const {
        handleClose,
        handleOpen,
        isOpen,
        includeLegacy,
        onPublicationClick,
        pins,
        onChangePins,
    } = props;

    const searchRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
    const [searchText, setSearchText] = useState("");
    const [enableResults, setEnableResults] = useState(false);

    const {
        loading: publicationGroupsLoading,
        publicationGroups,
        handleSearch,
        setSkip,
    } = usePublicationCodeGroups({
        includeLegacy,
        enabled: enableResults,
    });

    const { isOnline } = useNetworkInformation();

    const modalTitle = t("allItem", {
        item: t("publication_plural"),
    });
    const handleSearchClear = () => {
        if (CollectionUtils.hasValues(favoritedPublications)) {
            setEnableResults(false); // Ensures favorites are visible
        }
        setSearchText("");
        setSkip(0);
        handleSearch("");
    };
    const handleSearchTextChange = (e: ChangeEvent<HTMLInputElement>) => {
        setSearchText(e.currentTarget.value);
    };
    const { favorites } = usePublicationFavorites();

    useOutsideClick([modalRef, searchRef], () => {
        handleClose();
    });

    const handleSearchFieldClick = () => {
        // Auto-searches to populate with first take of codes instead of favorites if there are no favorites
        if (CollectionUtils.isEmpty(favoritedPublications)) {
            setEnableResults(true);
            setSkip(0);
            handleSearch("");
        }
        handleOpen();
    };

    const handleSearchClick = () => {
        handleSearch(searchText);
        if (searchText && !isOpen) {
            handleOpen();
        }
        setEnableResults(true);
    };

    const isMobile = useBreakpoint(
        Breakpoints.SmallTablet,
        BreakpointComparer.MaxWidth
    );

    const favoritedPublications = useMemo(
        () => favorites.map((upf) => upf.publication),
        [favorites]
    );

    const observerRef = useCallback(
        (node) => {
            if (node !== null && !publicationGroupsLoading) {
                if (intersectionObserverRef.current != null)
                    intersectionObserverRef.current.disconnect();

                intersectionObserverRef.current = new IntersectionObserver(
                    (entries: IntersectionObserverEntry[]) => {
                        if (entries.some((e) => e.isIntersecting))
                            setSkip((prev) => prev + 1);
                    },
                    {
                        threshold: 1.0,
                    }
                );
                intersectionObserverRef.current.observe(node);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    return (
        <div className={`${CSS_BASE_CLASS_NAME}`}>
            <div
                className={`${CSS_BASE_CLASS_NAME}__search${isOnline ? "" : "__disabled"}`}
                ref={searchRef}>
                {isMobile ? (
                    <Button
                        cssClassName={`${CSS_BASE_CLASS_NAME}__mobileSearchButton`}
                        style={ButtonStyles.Tertiary}
                        onClick={handleOpen}>
                        {t("findAPublication")}
                    </Button>
                ) : (
                    <SearchForm
                        disabled={!isOnline}
                        onClear={handleSearchClear}
                        onSearchClick={handleSearchClick}
                        onSearchTextChange={handleSearchTextChange}
                        onClick={handleSearchFieldClick}
                        placeholder={
                            isOpen
                                ? t(
                                      "publicationSelectionModal-searchPlaceholder"
                                  )
                                : t("findAPublication")
                        }
                        searchText={searchText}
                        style={SearchFormStyles.TertiaryAlt}
                        dataTestId="page--publication-list-search"
                    />
                )}
            </div>
            <>
                {isOpen && !isMobile && (
                    <div
                        aria-label={modalTitle}
                        className={`${CSS_BASE_CLASS_NAME}__modal`}>
                        <div>
                            <div className="c-modal__content" ref={modalRef}>
                                <div className={`${CSS_BASE_CLASS_NAME}__body`}>
                                    <PublicationListSearchContents
                                        pins={pins}
                                        enableResults={enableResults}
                                        publicationGroups={publicationGroups}
                                        publicationGroupsLoading={
                                            publicationGroupsLoading
                                        }
                                        favoritedPublications={
                                            favoritedPublications
                                        }
                                        observerRef={observerRef}
                                        onPublicationClick={onPublicationClick}
                                        onChangePins={onChangePins}
                                    />
                                </div>
                                <div
                                    className={`${CSS_BASE_CLASS_NAME}__footer`}>
                                    <Button
                                        onClick={handleClose}
                                        style={ButtonStyles.Tertiary}>
                                        {t("close")}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </>
            <Modal
                cssClassName={`${CSS_BASE_CLASS_NAME}__mobileContent`}
                type={ModalTypes.Bottom}
                closeDialog={handleClose}
                isVisible={isOpen && isMobile}
                label={""}>
                <SearchForm
                    disabled={!isOnline}
                    onClear={handleSearchClear}
                    onSearchClick={handleSearchClick}
                    onSearchTextChange={handleSearchTextChange}
                    onClick={handleOpen}
                    placeholder={
                        isOpen
                            ? t("publicationSelectionModal-searchPlaceholder")
                            : t("findAPublication")
                    }
                    searchText={searchText}
                    style={SearchFormStyles.TertiaryAlt}
                    dataTestId="page--publication-list-search"
                />
                <div className={`${CSS_BASE_CLASS_NAME}__mobileList`}>
                    <PublicationListSearchContents
                        pins={pins}
                        enableResults={enableResults}
                        publicationGroups={publicationGroups}
                        publicationGroupsLoading={publicationGroupsLoading}
                        favoritedPublications={favoritedPublications}
                        observerRef={observerRef}
                        onPublicationClick={onPublicationClick}
                        onChangePins={onChangePins}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default PublicationListSearch;
